import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { IconButton } from "../components/IconButton";
import { IconCameraAdd, IconEdit, IconLogout } from "../components/icons";
import { BiometricSecuritySection } from "../components/BiometricSecuritySection";
import { developerAvatarSrc, type Developer, devApi } from "../developer/api";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return "M";
  const last = parts[parts.length - 1];
  if (!last || parts.length === 1) return first.slice(0, 2).toUpperCase();
  return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase();
}

async function compressImage(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const max = 512;
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not process that image.");
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
  if (dataUrl.length > 500_000) throw new Error("Choose a smaller photo.");
  return dataUrl;
}

export function ProfilePage({
  me,
  onLogout,
  onAccountUpdated,
}: {
  me: Developer;
  onLogout: () => void;
  onAccountUpdated?: (developer: Developer) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(me.name);
  const [company, setCompany] = useState(me.company ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [brokenAvatar, setBrokenAvatar] = useState(false);

  const avatarSrc = preview || (!brokenAvatar ? developerAvatarSrc(me) : null);

  const saveProfile = async (ev: FormEvent) => {
    ev.preventDefault();
    const nextName = name.trim();
    if (!nextName) {
      setError("Enter your name.");
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const body = await devApi<{ developer: Developer }>("/api/developers/account", {
        method: "PATCH",
        body: JSON.stringify({ name: nextName, company: company.trim() || null }),
      });
      onAccountUpdated?.(body.developer);
      setEditing(false);
      setOk("Profile updated.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update profile");
    } finally {
      setBusy(false);
    }
  };

  const onPickPhoto = async (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Choose a JPG or PNG photo.");
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      const avatar_data = await compressImage(file);
      setPreview(avatar_data);
      setBrokenAvatar(false);
      const body = await devApi<{ developer: Developer }>("/api/developers/account", {
        method: "PATCH",
        body: JSON.stringify({ avatar_data }),
      });
      onAccountUpdated?.(body.developer);
      setOk("Profile photo updated.");
    } catch (err) {
      setPreview(null);
      setError(err instanceof Error ? err.message : "Could not update photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mobile-page profile-page">
      <section className="profile-hero">
        <button type="button" className="profile-avatar-btn" onClick={() => fileRef.current?.click()} disabled={busy} aria-label="Change profile photo">
          {avatarSrc ? <img src={avatarSrc} alt="" onError={() => setBrokenAvatar(true)} /> : <span aria-hidden="true">{initials(me.name)}</span>}
          <span className="profile-avatar-badge">
            <IconCameraAdd />
          </span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" hidden onChange={(ev) => void onPickPhoto(ev)} />
        <h2>{me.name}</h2>
        <p>{me.email}</p>
        <IconButton label="Edit profile" className="header-action profile-edit-btn" onClick={() => setEditing((v) => !v)}>
          <IconEdit />
          <span className="icon-btn-label">{editing ? "Close" : "Edit Profile"}</span>
        </IconButton>
      </section>

      {editing ? (
        <form className="device-form profile-edit-form" onSubmit={(ev) => void saveProfile(ev)}>
          <label>
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />
          </label>
          <label>
            Company
            <input value={company} onChange={(e) => setCompany(e.target.value)} autoComplete="organization" />
          </label>
          {error && <p className="form-error">{error}</p>}
          {ok && <p className="form-success">{ok}</p>}
          <button type="submit" className="primary" disabled={busy}>
            {busy ? "Saving…" : "Save profile"}
          </button>
        </form>
      ) : (
        <ul className="profile-meta">
          <li>
            <span>Role</span>
            <strong>Master administrator</strong>
          </li>
          <li>
            <span>Status</span>
            <strong>{me.status}</strong>
          </li>
          <li>
            <span>Company</span>
            <strong>{me.company || "—"}</strong>
          </li>
        </ul>
      )}

      {!editing && error && <p className="form-error">{error}</p>}
      {!editing && ok && <p className="form-success">{ok}</p>}

      {!editing && <BiometricSecuritySection email={me.email} />}

      <button type="button" className="profile-logout" onClick={() => setConfirmLogout(true)}>
        <IconLogout />
        Logout
      </button>

      <ConfirmDialog
        open={confirmLogout}
        danger
        title="Log out?"
        message="You will need to sign in again to manage cameras and API keys."
        confirmLabel="Logout"
        onCancel={() => setConfirmLogout(false)}
        onConfirm={() => {
          setConfirmLogout(false);
          onLogout();
        }}
      />
    </div>
  );
}
